import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  LinearProgress,
  Avatar,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material'
import {
  Visibility,
  Message,
  CheckCircle,
  Schedule,
  Warning,
  Search,
  Sort,
  ViewList,
  ViewModule,
  GridView,
  Person,
  LocationOn,
  Agriculture,
  Pets,
  TrendingUp
} from '@mui/icons-material'

interface Production {
  id: string
  farmer_name: string
  farmer_id: string
  production_name: string
  production_type: 'crop' | 'animal'
  variety_name: string
  start_date: string
  expected_harvest: string
  current_stage: string
  progress_percentage: number
  status: 'active' | 'completed' | 'delayed' | 'at_risk'
  location: string
  total_stages: number
  completed_stages: number
  issues_count: number
  last_update: string
}

const Productions: React.FC = () => {
  const navigate = useNavigate()
  const [productions, setProductions] = useState<Production[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedProduction, setSelectedProduction] = useState<Production | null>(null)
  const [interventionDialog, setInterventionDialog] = useState(false)
  const [interventionMessage, setInterventionMessage] = useState('')
  const [interventionType, setInterventionType] = useState('advice')
  
  // New state for enhanced features
  const [viewMode, setViewMode] = useState<'table' | 'cards' | 'grid'>('table')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'date' | 'farmer' | 'progress' | 'status'>('date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterLocation, setFilterLocation] = useState<string>('all')

  const fetchProductions = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      const response = await fetch(`${API_BASE_URL}/farmer-productions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      // Transform backend data to match our interface
      const transformedProductions: Production[] = data.productions?.map((prod: any) => ({
        id: prod.id || prod.production_id,
        farmer_name: prod.farmer?.name || prod.farmer_name || 'Unknown Farmer',
        farmer_id: prod.farmer_id,
        production_name: prod.production_name || prod.name,
        production_type: prod.production_type || (prod.template?.category === 'crop' ? 'crop' : 'animal'),
        variety_name: prod.variety_name || prod.template?.variety_name || 'Unknown Variety',
        start_date: prod.start_date || prod.created_at,
        expected_harvest: prod.expected_harvest_date || prod.expected_end_date,
        current_stage: prod.current_stage?.name || prod.current_stage || 'Not Started',
        progress_percentage: prod.progress_percentage || 0,
        status: prod.status === 'active' ? 'active' : 
                prod.status === 'completed' ? 'completed' :
                prod.progress_percentage < 50 ? 'at_risk' : 'active',
        location: prod.location || prod.farmer?.location || 'Unknown Location',
        total_stages: prod.total_stages || prod.stages?.length || 0,
        completed_stages: prod.completed_stages || 0,
        issues_count: prod.issues_count || 0,
        last_update: prod.updated_at || prod.last_update || new Date().toISOString()
      })) || []

      setProductions(transformedProductions)
    } catch (error) {
      console.error('Error fetching productions:', error)
      // Set empty array on error
      setProductions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProductions()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'completed': return 'primary'
      case 'delayed': return 'warning'
      case 'at_risk': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle />
      case 'completed': return <CheckCircle />
      case 'delayed': return <Schedule />
      case 'at_risk': return <Warning />
      default: return <Schedule />
    }
  }

  const handleSendIntervention = async () => {
    if (selectedProduction && interventionMessage.trim()) {
      try {
        const token = localStorage.getItem('token')
        
        // Step 1: Create or get admin-farmer conversation
        const conversationResponse = await fetch(`${API_BASE_URL}/admin-messaging/conversations`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            farmerId: selectedProduction.farmer_id,
            productionId: selectedProduction.id,
            subject: `${interventionType.charAt(0).toUpperCase() + interventionType.slice(1)} - ${selectedProduction.production_name}`
          })
        })

        if (!conversationResponse.ok) {
          const errorData = await conversationResponse.json()
          throw new Error(`Failed to create conversation: ${errorData.message || conversationResponse.status}`)
        }

        const conversationResult = await conversationResponse.json()
        const conversationId = conversationResult.data.conversation.id

        // Step 2: Send the intervention message
        const messageResponse = await fetch(`${API_BASE_URL}/admin-messaging/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            conversationId: conversationId,
            message: interventionMessage,
            messageType: 'text',
            interventionType: interventionType
          })
        })

        if (!messageResponse.ok) {
          const errorData = await messageResponse.json()
          throw new Error(`Failed to send message: ${errorData.message || messageResponse.status}`)
        }

        const messageResult = await messageResponse.json()
        console.log('Intervention sent successfully:', messageResult)
        
        // Show success message
        alert(`${interventionType.charAt(0).toUpperCase() + interventionType.slice(1)} message sent successfully to ${selectedProduction.farmer_name}!`)
        
      } catch (error) {
        console.error('Error sending intervention:', error)
        alert(`Failed to send message to farmer: ${error instanceof Error ? error.message : 'Please try again.'}`)
      }
      
      setInterventionDialog(false)
      setInterventionMessage('')
      setSelectedProduction(null)
    }
  }

  // Enhanced filtering and sorting logic
  const getFilteredAndSortedProductions = () => {
    let filtered = productions.filter(production => {
      // Tab filtering
      let tabMatch = true
      switch (selectedTab) {
        case 0: tabMatch = true; break // All
        case 1: tabMatch = production.status === 'active'; break // Active
        case 2: tabMatch = production.status === 'at_risk' || production.issues_count > 0; break // Need Attention
        case 3: tabMatch = production.status === 'completed'; break // Completed
        default: tabMatch = true
      }

      // Search filtering
      const searchMatch = searchQuery === '' || 
        production.farmer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        production.production_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        production.variety_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        production.location.toLowerCase().includes(searchQuery.toLowerCase())

      // Status filtering
      const statusMatch = filterStatus === 'all' || production.status === filterStatus

      // Type filtering
      const typeMatch = filterType === 'all' || production.production_type === filterType

      // Location filtering (simplified - could be enhanced with proper location data)
      const locationMatch = filterLocation === 'all' || 
        production.location.toLowerCase().includes(filterLocation.toLowerCase())

      return tabMatch && searchMatch && statusMatch && typeMatch && locationMatch
    })

    // Sorting
    filtered.sort((a, b) => {
      let comparison = 0
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
          break
        case 'farmer':
          comparison = a.farmer_name.localeCompare(b.farmer_name)
          break
        case 'progress':
          comparison = a.progress_percentage - b.progress_percentage
          break
        case 'status':
          comparison = a.status.localeCompare(b.status)
          break
        default:
          comparison = 0
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

    return filtered
  }

  const filteredProductions = getFilteredAndSortedProductions()

  const stats = {
    total: productions.length,
    active: productions.filter(p => p.status === 'active').length,
    atRisk: productions.filter(p => p.status === 'at_risk' || p.issues_count > 0).length,
    completed: productions.filter(p => p.status === 'completed').length
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Production Management</Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Production Management
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Monitor and support farmer production cycles across the platform
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Agriculture color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Productions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.active}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Productions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Warning color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.atRisk}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Need Attention
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.completed}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search, Filters, and View Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search productions, farmers, varieties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Sort */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value as any)}
                >
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="farmer">Farmer</MenuItem>
                  <MenuItem value="progress">Progress</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Sort Order */}
            <Grid item xs={12} sm={6} md={2}>
              <ToggleButtonGroup
                value={sortOrder}
                exclusive
                onChange={(_, value) => value && setSortOrder(value)}
                size="small"
              >
                <ToggleButton value="asc">
                  <Tooltip title="Ascending">
                    <Sort />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="desc">
                  <Tooltip title="Descending">
                    <Sort style={{ transform: 'rotate(180deg)' }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Filters */}
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="at_risk">At Risk</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="delayed">Delayed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* View Mode */}
            <Grid item xs={12} sm={8} md={2}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, value) => value && setViewMode(value)}
                size="small"
              >
                <ToggleButton value="table">
                  <Tooltip title="Table View">
                    <ViewList />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="cards">
                  <Tooltip title="Card View">
                    <ViewModule />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <GridView />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          {/* Additional Filters Row */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Production Type</InputLabel>
                <Select
                  value={filterType}
                  label="Production Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="crop">Crops</MenuItem>
                  <MenuItem value="animal">Animals</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter by location..."
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredProductions.length} of {productions.length} productions
                </Typography>
                {(searchQuery || filterStatus !== 'all' || filterType !== 'all' || filterLocation) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSearchQuery('')
                      setFilterStatus('all')
                      setFilterType('all')
                      setFilterLocation('')
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs for filtering */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label={`All (${stats.total})`} />
          <Tab label={`Active (${stats.active})`} />
          <Tab label={`Need Attention (${stats.atRisk})`} />
          <Tab label={`Completed (${stats.completed})`} />
        </Tabs>
      </Box>

      {/* Productions Display */}
      {viewMode === 'table' ? (
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Farmer</TableCell>
                    <TableCell>Production</TableCell>
                    <TableCell>Type & Variety</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Issues</TableCell>
                    <TableCell>Last Update</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProductions.map((production) => (
                    <TableRow key={production.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {production.farmer_name}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              <LocationOn fontSize="small" color="disabled" />
                              <Typography variant="caption" color="text.secondary">
                                {production.location}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {production.production_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Started: {new Date(production.start_date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={production.production_type === 'crop' ? <Agriculture /> : <Pets />}
                          label={production.production_type}
                          size="small"
                          color={production.production_type === 'crop' ? 'success' : 'info'}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="body2">
                          {production.variety_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 120 }}>
                          <LinearProgress
                            variant="determinate"
                            value={production.progress_percentage}
                            sx={{ mt: 1 }}
                          />
                          <Typography variant="caption">
                            {production.progress_percentage}% complete
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(production.status)}
                          label={production.status === 'at_risk' ? 'At Risk' : 
                                 production.status.charAt(0).toUpperCase() + production.status.slice(1)}
                          color={getStatusColor(production.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {production.issues_count > 0 ? (
                          <Chip
                            label={`${production.issues_count} issues`}
                            color="warning"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No issues
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(production.last_update).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/farmers/${production.farmer_id}`)}
                          title="View Farmer Profile"
                        >
                          <Person />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/production-plans/${production.id}`)}
                          title="View Production Details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/productions/${production.id}/messages`)}
                          title="Open Messages with Farmer"
                        >
                          <Message />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : viewMode === 'cards' ? (
        <Grid container spacing={3}>
          {filteredProductions.map((production) => (
            <Grid item xs={12} sm={6} md={4} key={production.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  {/* Header */}
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      <Person />
                    </Avatar>
                    <Box flex={1}>
                      <Typography variant="h6" noWrap>
                        {production.farmer_name}
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <LocationOn fontSize="small" color="disabled" />
                        <Typography variant="caption" color="text.secondary">
                          {production.location}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Production Info */}
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {production.production_name}
                  </Typography>
                  
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Chip 
                      icon={production.production_type === 'crop' ? <Agriculture /> : <Pets />}
                      label={production.production_type}
                      size="small"
                      color={production.production_type === 'crop' ? 'success' : 'info'}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {production.variety_name}
                    </Typography>
                  </Box>

                  {/* Progress */}
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">Progress</Typography>
                      <Typography variant="body2">{production.progress_percentage}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={production.progress_percentage}
                    />
                  </Box>

                  {/* Status and Issues */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Chip
                      icon={getStatusIcon(production.status)}
                      label={production.status === 'at_risk' ? 'At Risk' : 
                             production.status.charAt(0).toUpperCase() + production.status.slice(1)}
                      color={getStatusColor(production.status) as any}
                      size="small"
                    />
                    {production.issues_count > 0 && (
                      <Chip
                        label={`${production.issues_count} issues`}
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>

                  {/* Actions */}
                  <Box display="flex" gap={1} flexDirection="column">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Person />}
                      onClick={() => navigate(`/farmers/${production.farmer_id}`)}
                      fullWidth
                    >
                      View Farmer Profile
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/production-plans/${production.id}`)}
                      fullWidth
                    >
                      View Production
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Message />}
                      onClick={() => navigate(`/productions/${production.id}/messages`)}
                      fullWidth
                    >
                      Open Messages
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        // Grid View - Compact cards
        <Grid container spacing={2}>
          {filteredProductions.map((production) => (
            <Grid item xs={12} sm={6} md={3} key={production.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                      {production.production_type === 'crop' ? <Agriculture /> : <Pets />}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {production.farmer_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {production.production_name}
                      </Typography>
                    </Box>
                  </Box>

                  <LinearProgress
                    variant="determinate"
                    value={production.progress_percentage}
                    sx={{ mb: 1 }}
                  />

                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="caption">
                      {production.progress_percentage}%
                    </Typography>
                    <Chip
                      label={production.status === 'at_risk' ? 'At Risk' : 
                             production.status.charAt(0).toUpperCase() + production.status.slice(1)}
                      color={getStatusColor(production.status) as any}
                      size="small"
                      sx={{ fontSize: '0.6rem', height: 20 }}
                    />
                  </Box>

                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/farmers/${production.farmer_id}`)}
                      title="View Farmer Profile"
                    >
                      <Person fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/production-plans/${production.id}`)}
                      title="View Details"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/productions/${production.id}/messages`)}
                      title="Open Messages"
                    >
                      <Message fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Intervention Dialog */}
      <Dialog 
        open={interventionDialog} 
        onClose={() => setInterventionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Send Message to {selectedProduction?.farmer_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              label="Message Type"
              value={interventionType}
              onChange={(e) => setInterventionType(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            >
              <MenuItem value="advice">Advisory Message</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="support">Technical Support</MenuItem>
              <MenuItem value="resource">Resource Allocation</MenuItem>
            </TextField>
            
            <TextField
              label="Message"
              multiline
              rows={4}
              value={interventionMessage}
              onChange={(e) => setInterventionMessage(e.target.value)}
              fullWidth
              placeholder="Enter your intervention message..."
            />
            
            {selectedProduction && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Production: {selectedProduction.production_name} - 
                Current Stage: {selectedProduction.current_stage} - 
                Progress: {selectedProduction.progress_percentage}%
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterventionDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSendIntervention}
            variant="contained"
            disabled={!interventionMessage.trim()}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Productions
